import type { ModelClientForRecord } from '@app/api/Clients/types';
import type { ModelProductForSale } from '@app/api/Products/types';
import type { PhoneState } from '@app/components/Phone/types';
import type { SubscriptionQuantityLimit } from '@app/components/RecordModal/components/RecordDetails/utils/subscriptionQuantityLimit';
import type { ContentProps } from '@app/components/RecordModal/types';
import type { Warnings } from '@app/services/rtkQuery/Records/types';
import type {
  ModelRecordProductDiscount,
  NSmallGlobalEntities,
  TEmployeeOptions,
  TTag,
} from '@app/types';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { FormInstance, FormListFieldData } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

export interface Service {
  discountCurrency: number;
  discountPercent: number;
  totalDiscount?: number;
  price: number;
  quantity: number;
  service: string | null;
  upsaleEmployee: number | null;
  duration: number;
  isExcludedFromLoyaltyDiscount: boolean;
  /** Название услуги — для уведомлений о строке */
  name?: string;
  /** Услуга с абонемента клиента: цена 0, цена и скидка не меняются */
  clientSubscriptionId?: number | null;
}

export interface ServiceOption extends DefaultOptionType {
  price: number;
  duration: number;
}

export interface ServiceGroup {
  label: string;
  options: Array<ServiceOption>;
}
export interface RecordDetailsForm {
  Status: ClientStatusEnum;
  Phone: PhoneState;
  IsPhoneVerified: boolean;
  NameFirst: string;
  NameLast: string;
  services: Array<Service>;
  products: Array<ProductSaleDraftItem>;
  subscriptions: Array<SubscriptionSaleDraftItem>;
  certificates: Array<CertificateSaleDraftItem>;
  Date: Dayjs | null;
  TimeStart: string;
  TimeEnd: string;
  DurationHour: number;
  DurationMinute: number;
  Comment: string;
  Tags: Array<number>;
  Color: string | null;
  IsLocked: boolean;
  Employee: number | null;
  EmployeePayoff: number | null;
  /** Значения важных доп. полей клиента: FieldId -> значение */
  ClientFieldValues?: Record<string, string>;
}

export enum RecordTypeEnum {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
  OPERATIVE = 'operational',
  KROSS = 'cross-record',
  ONLINE = 'online-record',
  NONE = 'none',
}

export enum ClientStatusEnum {
  WAITING = 'waiting',
  CONFIRMED = 'confirmed',
  VISITED = 'visited',
  NOVISIT = 'novisit',
}

export enum ClientStatusLabelEnum {
  WAITING_LABEL = 'Ожидание клиента',
  CONFIRMED_LABEL = 'Клиент подтвердил',
  VISITED_LABEL = 'Клиент пришел',
  NOVISIT_LABEL = 'Клиент не пришел',
}

export interface TotalDuration {
  hours: number;
  minutes: number;
}
export interface ClientStatuses {
  id: number;
  status: ClientStatusEnum;
  label: ClientStatusLabelEnum;
  color: string;
  icon: IconDefinition;
}

export interface ClientStatus extends ClientStatuses {
  selected?: boolean;
  disabled?: boolean;
  onSelect(status: ClientStatusEnum): void;
}
export interface StatusesProps {
  items: Array<ClientStatuses>;
  selectedItem: ClientStatusEnum | null;
  onSelect(status: ClientStatusEnum): void;
  checkDisabledStatus(status: ClientStatusEnum): boolean;
  disabled?: boolean;
}
interface AutoAddProps {
  add(): void;
  fields: Array<FormListFieldData>;
}

interface ServicesProps {
  totalDuration: TotalDuration;
  serviceOptions: Array<ServiceGroup>;
  branchUserOptions: BranchUserOption[];
  /** Предел количества строки с абонемента, null — строка не с абонемента */
  getSubscriptionQuantityLimit(
    service: Service
  ): SubscriptionQuantityLimit | null;
  disabled?: boolean;
}

export interface ProductSaleDraftItem {
  id: string;
  warehouseId?: number;
  productId?: number;
  productName?: string | null;
  quantity?: number;
  price?: number;
  countAvailable?: number;
  discountCurrency: number;
  discountPercent: number;
  sellerId?: number | null;
  groupId?: string | null;
  discounts: ModelRecordProductDiscount[];
  cost: number;
  paid: number;
  totalDiscount: number;
  isExcludedFromLoyaltyDiscount: boolean;
  // Immutable identity of the product as it was persisted on the backend.
  // Set only when mapping an existing record product into a draft row and
  // never mutated when the user changes warehouse or product. Used to decide
  // whether the original GroupId can be reused in the payload.
  originalGroupId?: string | null;
  originalWarehouseId?: number;
  originalProductId?: number;
}

/** Строка абонемента в окне записи (продажа абонемента через запись) */
export interface SubscriptionSaleDraftItem {
  /** Ключ строки в UI */
  id: string;
  /** ID проданного абонемента клиента, 0 — новая строка (ещё не продана) */
  saleId: number;
  /** ID абонемента из реестра */
  subscriptionId?: number;
  name?: string | null;
  /** Цена абонемента без скидки записи */
  price: number;
  /** Скидка в рублях — уходит на бэкенд */
  discountCurrency: number;
  discountPercent: number;
  sellerId: number | null;
  /** Проданный абонемент не оплачен — по бэкенду его можно менять и удалять */
  canEditAndDelete: boolean;
}

/**
 * Строка сертификата в окне записи (продажа сертификата через запись).
 * Пока только UI: методов загрузки и сохранения на бэкенде ещё нет.
 */
export interface CertificateSaleDraftItem {
  /** Ключ строки в UI */
  id: string;
  /** ID проданного сертификата клиента, 0 — новая строка (ещё не продана) */
  saleId: number;
  /** ID сертификата из реестра */
  certificateId?: number;
  name?: string | null;
  /** Номинал сертификата из реестра */
  nominal: number;
  /** Цена продажи без скидки, по умолчанию равна номиналу */
  price: number;
  discountCurrency: number;
  discountPercent: number;
  sellerId: number | null;
  /** Проданный сертификат не оплачен — его можно менять и удалять */
  canEditAndDelete: boolean;
}

export type SelectedWarehouseProduct = ModelProductForSale;

interface ProductSaleItemsProps {
  warehouses: NSmallGlobalEntities.ModelItem[];
  branchUserOptions: BranchUserOption[];
  activeBranchId: number | null;
  items: ProductSaleDraftItem[];
  onChange(items: ProductSaleDraftItem[]): void;
  disabled?: boolean;
}

export type RecordClientSearchResult = ModelClientForRecord & {
  DateBirth?: string | null;
  Email?: string | null;
};

export interface BranchUserOption {
  value: number;
  label: string;
}

export interface TagOption {
  value: number;
  label: string;
  color: string;
  backgroundColor: string;
  icon: string;
}

interface EmployeeProps {
  mainEmployeeOptions: TEmployeeOptions[];
  tagOptions: TagOption[];
  toggleLock(): void;
  showDoubleRecordSelects: boolean;
  showDoubleRecordButton: boolean;
  handleAddAdditionalEmployee(): void;
  handleRemoveAdditionalEmployee(index: number): void;
  handleAdditionalEmployeeChange(index: number, value: number): void;
  handleDuplicateClick(): void;
  additinalEmployeeOptions: TEmployeeOptions[];
  additionalEmployees: (number | null)[];
  mainEmployeeSelected: number | string | null;
  onChangeTags?(tags: number[]): void;
  showEmployeePayoff: boolean;
  disabled?: boolean;
  isFormDisabled?: boolean;
  onSelectEmployee: (employeeId: number) => Promise<void>;
  isFetchingGetClientServicesByEmployeeId: boolean;
  isLoadingGetClientServicesByEmployeeId: boolean;
  tags: TTag[];
  setTags: React.Dispatch<React.SetStateAction<TTag[]>>;
  allTags: TTag[];
}

export interface UseRecordDetailsProps {
  confirmUnsavedData: ContentProps['confirmUnsavedData'];
  confirmWarnings: ContentProps['confirmWarnings'];
}
export interface UseRecordDetailsEffectsProps {
  initForm(): void;
  resetForm(): void;
  importantEmployeeHandler(): void;
  isOpen: boolean;
  handleConfirm(): void;
  checkUnsavedData(): void;
  isInitialized: boolean;
  hasChanges: boolean;
  getEmployeesByServicesHandler(): Promise<void>;
  checkRecordTimesHandler(): Promise<Warnings[] | undefined>;
  employeePayoffHandler(): void;
  getAllTagsHandler(): Promise<void>;
  closeRecordDetails(): void;
  restoreSavedClientFields(): void;
}

export interface useEmployeeEffectsProps {
  checkRecordTimesHandler(): Promise<void>;
}
export interface SelectedEmployee {
  RecordId: number;
  EmployeeId: number;
}

export interface UseEmployeeProps {
  disabled?: boolean;
  isFetchingGetClientServicesByEmployeeId: boolean;
  isLoadingGetClientServicesByEmployeeId: boolean;
}

export interface UseServicesProps {
  serviceOptions: Array<ServiceGroup>;
  disabled?: boolean;
}

export interface UseSubscriptionQuantityLimitsProps {
  clientId: number | null;
  activeBranchId: number | null;
  /** Строки «Услуг» записи на момент последнего сохранения или загрузки */
  savedServices: Service[];
}

export interface UseClientSubscriptionServicesProps {
  form: FormInstance<RecordDetailsForm>;
  /** Услуги выбранного исполнителя */
  serviceOptions: Array<ServiceGroup>;
}

export interface EmployeeTabsProps {}

export type EmployeeTabsType = FC<EmployeeTabsProps>;
export type EmployeeType = FC<EmployeeProps>;
export type AutoAddType = FC<AutoAddProps>;
export type ServicesType = FC<ServicesProps>;
export type ProductSaleItemsType = FC<ProductSaleItemsProps>;
export type StatusesType = FC<StatusesProps>;
export type StatusType = FC<ClientStatus>;
